import {registerPlugin} from "@capacitor/core";

const ANDROID_TAB_IDS = {
  DIARY: "DIARY",
  CHALLENGES: "CHALLENGES",
  ACCOUNT: "ACCOUNT"
};

interface TabBarPluginInterface {
  changeTab: (options: {tab: string}) => Promise<void>;
  setBottomBarVisibility: (options: {show: boolean}) => Promise<void>;
}

const TabBarPlugin = registerPlugin<TabBarPluginInterface>("TabBarPlugin");

/**
 * Pages reached through the DIARY tab's WebView fallback (Equipment, Trash, Refused
 * Activities, ...) carry a `mapFromDiary` state flag, mirroring ActivityPage's
 * safeNavigateBack. Their own back buttons need to hand control back to native the same
 * way — the WebView's SPA history stack never gets real entries from normal browsing when
 * reached this way (it's frozen at whatever loaded on cold boot), so a plain history back/push
 * would land on a stale or wrong route instead of back in Diary.
 */
export function navigateBackToNativeDiary(): void {
  TabBarPlugin.setBottomBarVisibility({show: true}).catch(() => {});
  TabBarPlugin.changeTab({tab: ANDROID_TAB_IDS.DIARY}).catch(() => {});
}

const PAGE_PATHS = {
  challenges: "/challenges",
  settings: "/android-settings",
  myAccount: "/my-account",
  redeemCode: "/redeem-code",
  favorites: "/favorites",
  diary: "/activities",
  oldDiary: "/diary",
  statistics: "/statistics",
  compareActivities: "/compare-activities",
  activity: "/activity",
  trash: "/trash",
  refusedActivities: "/refused-activities",
  equipments: "/equipments"
};

function normalizePathname(pathname: string = ""): string {
  if (pathname === "/") {
    return pathname;
  }
  return pathname.replace(/\/+$/, "");
}

function pathStartsWith(pathname: string, basePath: string): boolean {
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

/**
 * Resolves the Android native tab for a given web route.
 *
 * Some routes (e.g. `/around-me`) are shared across native tabs, so we return
 * `null` there to avoid forcing the wrong tab selection.
 *
 * @param {object} location react-router location (`pathname`, `state`)
 * @return {?string} Android tab id, or null when the route is ambiguous
 */
export function getAndroidTabForRoute(location: any = {}): string | null {
  const pathname = normalizePathname(location?.pathname || "");
  const state = location?.state || {};

  if (!pathname) {
    return null;
  }

  if (pathStartsWith(pathname, PAGE_PATHS.challenges) || state.mapFromChallenge) {
    return ANDROID_TAB_IDS.CHALLENGES;
  }

  if (
    pathStartsWith(pathname, PAGE_PATHS.settings) ||
    pathStartsWith(pathname, PAGE_PATHS.myAccount) ||
    pathStartsWith(pathname, PAGE_PATHS.redeemCode) ||
    pathStartsWith(pathname, PAGE_PATHS.favorites)
  ) {
    return ANDROID_TAB_IDS.ACCOUNT;
  }

  if (
    pathStartsWith(pathname, PAGE_PATHS.diary) ||
    pathStartsWith(pathname, PAGE_PATHS.oldDiary) ||
    pathStartsWith(pathname, PAGE_PATHS.statistics) ||
    pathStartsWith(pathname, PAGE_PATHS.compareActivities) ||
    pathStartsWith(pathname, PAGE_PATHS.activity) ||
    pathStartsWith(pathname, PAGE_PATHS.trash) ||
    pathStartsWith(pathname, PAGE_PATHS.refusedActivities) ||
    pathStartsWith(pathname, PAGE_PATHS.equipments) ||
    pathStartsWith(pathname, "/users")
  ) {
    return ANDROID_TAB_IDS.DIARY;
  }

  return null;
}
